import Card from '../components/card/Card';
import { useEffect, useState } from 'react';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PostBalanceActivity from '../organisms/Risk/PostBalance/PostBalanceActivity';
import Icon from '../components/icon/Icon';
import CheckLight from '../organisms/Risk/CheckLight/Checklight';
import { getApplication } from '../services/ApplicationService';
import { balanceInfo, clientData, riskData } from '../types/interfaces';
import { Toast, useToast } from '../components/toast/toast';
import { BALANCE_STATUS_MESSAGES, getBalanceProcessingErrorMessage } from '../utils/balanceReadStatusUtils';
import {
  getBalanceSheetsByCuit,
  getBalanceInfoByCode,
  postRiskData,
  putRiskData,
  getDebts,
  saveDebts,
  updateDebts,
} from '../services/RiskService';
import BalanceLoader from '../organisms/Risk/BalanceLoader';
import RiskResume from '../organisms/Risk/Resume';
import { initialClientData, RISK_CODES } from '../utils/Constants';
import {
  capitalize,
  hasData,
  hasError,
  isSuccessfulResponse,
  getEnabledRiskCodes,
} from '../utils/Utils';
import { standardizeBalanceData } from '../utils/BalanceDataUtils';
import { StandardizedBalanceData } from '../types/interfaces';
import Step from '../components/Step/Step';
import useStatus from '../hooks/useStatus';
import useStepNavigation from '../hooks/useStepNavigation';
import { routes } from '../routes/routePaths';
import { cuitMask } from '../utils/Masks';

const RiskAnalysis = ({
  showStepBar = true,
  showOnlyRiskResume = false,
  hideCommiteInfo = false,
}: {
  showStepBar?: boolean;
  showOnlyRiskResume?: boolean;
  hideCommiteInfo?: boolean;
}): JSX.Element => {
  const { cuit, id } = useParams();
  const { state } = useLocation();
  const { status } = state ? state : '';
  const { isInCommittee } = useStatus({ applicationStatus: parseInt(status) });
  const [currentStep, setCurrentStep] = useState(state ? 1 : 1);
  const navigate = useNavigate();
  const [riskData, setRiskData] = useState<riskData | undefined>(undefined);
  const [balanceData, setBalanceData] = useState<StandardizedBalanceData | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [balanceInfo, setBalanceInfo] = useState<balanceInfo | undefined>(
    undefined
  );
  const [purchasesSalesData, setPurchasesSalesData] = useState({});
  const [postBalanceData, setPostBalanceData] = useState({});
  const [client, setClient] = useState<clientData>(initialClientData);
  const [localBalanceId, setLocalBalanceId] = useState<number>(0);
  const [debts, setDebts] = useState<any>({});
  const [riskAnalysisId, setRiskAnaliysisId] = useState<number>();

  // Hook para manejar la navegación automática de pasos
  const {
    maxStepReached,
    updateMaxStepReached,
    navigateToStep,
    goToNextStep,
    goToPreviousStep,
  } = useStepNavigation({
    cuit,
    id,
    moduleName: 'riskAnalysis',
    totalSteps: 5,
    currentStep,
    setCurrentStep,
  });

  useEffect(() => {
    if (cuit && id) {
      getApplication(cuit, id).then((response) => {
        if (hasData(response)) {
          setClient(response.data);
          setPostBalanceData(response.data.post_balance_sheet_items);

          if (response.data.balance_sheet_code) {
            getBalanceInfoFromCode(cuit, response.data.balance_sheet_code);
          } else {
            // No hay balance_sheet_code asignado, intentar obtener el más reciente
            getMostRecentBalanceSheets(cuit);
            // Si no hay balance_sheet_code, mostrar mensaje informativo
            useToast.info({
              title: 'No hay balance cargado',
              id: 'risk-toast',
              description: BALANCE_STATUS_MESSAGES.NO_BALANCE_PAGE,
              closeButton: true,
              autoClose: 8000,
            });
          }

          if (
            response.data.balance_processing_status === 'FAILED' &&
            response.data.balance_processing_error_code
          ) {
            const description = getBalanceProcessingErrorMessage(
              response.data.balance_processing_error_code,
              response.data.balance_processing_error_context
            );
            useToast.error({
              title: 'El balance falló al procesarse',
              id: 'risk-toast',
              description,
              closeButton: true,
              autoClose: 10000,
            });
          }

          if (response.data.risk_analysis) {
            setRiskAnaliysisId(response.data.risk_analysis.id);

            const expectedRiskCodes = getEnabledRiskCodes([
              RISK_CODES.nosis,
              RISK_CODES.casfog,
              RISK_CODES.bcra,
            ]);
            const savedRisksCodes: string[] =
              response.data.risk_analysis.risks.map(
                (risk: { risk_code: string }) => risk.risk_code
              );
            const hasAllRequiredRisks = expectedRiskCodes.every((code) =>
              savedRisksCodes.includes(code)
            );

            if (!hasAllRequiredRisks) {
              handleRiskData(getEnabledRiskCodes([
                RISK_CODES.nosis,
                RISK_CODES.casfog,
                RISK_CODES.bcra,
              ]));
            } else {
              setRiskData(response.data.risk_analysis.risks);
              setIsLoading(false);
            }
          } else {
            handleRiskData(getEnabledRiskCodes([
              RISK_CODES.nosis,
              RISK_CODES.casfog,
              RISK_CODES.bcra,
            ]));
          }
        }

        if (
          hasData(response) &&
          response.data.client_debt_analysis_code === null
        ) {
          saveDebts(cuit, id).then((response) => {
            if (hasData(response) && response.status === 201) {
              setDebts(response.data);
            }
          });
        } else {
          if (hasData(response)) {
            const code = response.data.client_debt_analysis_code;
            getDebts(cuit, code).then((response) => {
              if (hasData(response) && response.status === 200) {
                setDebts(response.data);
              } else if (hasError(response).isAnyError) {
                useToast.error({
                  title: 'Ocurrió un error al consultar la información.',
                  id: 'risk-toast',
                  description: hasError(response).isClientError
                    ? 'Error del cliente'
                    : 'Error del servidor',
                  closeButton: true,
                  autoClose: 5000,
                });
              }
            });
          }
        }
      });
    }
  }, [cuit, id]);

  const changeStep = (step: number) => {
    const canNavigate =
      maxStepReached >= 5 || isInCommittee || currentStep > step;

    if (canNavigate && balanceData !== undefined) {
      navigateToStep(step);
    }
  };

  const getBalanceInfoFromCode = (cuit: string, balanceSheetCode: string) => {
    getBalanceInfoByCode(cuit, balanceSheetCode).then((response) => {
      if (hasData(response) && response.status === 200) {
        // Usar la función utilitaria para estandarizar los datos
        const standardizedData = standardizeBalanceData(response.data);
        setBalanceData(standardizedData);
        setBalanceInfo({
          ...response.data,
          ...formatNumbers(response.data),
        });
        setLocalBalanceId(response.data.id);
      } else if (hasError(response).isAnyError) {
        useToast.error({
          title: 'Ocurrió un error al consultar la información del balance.',
          id: 'risk-toast',
          description: hasError(response).isClientError
            ? 'Error del cliente'
            : 'Error del servidor',
          closeButton: true,
          autoClose: 5000,
        });
      }
    });
  };

  const getMostRecentBalanceSheets = (cuit: string) => {
    getBalanceSheetsByCuit(cuit).then((response) => {
      if (hasData(response)) {
        if (isSuccessfulResponse(response)) {
          if (response.data.length > 0) {
            setBalanceInfo({
              ...response.data[0],
              ...formatNumbers(response.data[0]),
            });
            setLocalBalanceId(response.data[0].id);
          } else {
            // No hay balances disponibles, mostrar mensaje informativo
            useToast.info({
              title: 'No hay balance cargado',
              id: 'risk-toast',
              description: BALANCE_STATUS_MESSAGES.NO_BALANCE_PAGE,
              closeButton: true,
              autoClose: 8000,
            });
          }
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: hasError(response).isClientError
              ? 'Ocurrió un error al consultar la información del balance.'
              : 'Ocurrió un error inesperado. Intentá nuevamente.',
            id: 'risk-toast',
            description: hasError(response).isClientError
              ? 'Error del cliente'
              : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
      }
    });
  };

  const formatNumbers = (obj: Record<string, any>) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]): [string, any] => {
        if (typeof value === 'object' && value !== null) {
          return [key, formatNumbers(value)];
        }
        return [key, value];
      })
    );
  };
  const handleRiskData = (riskCodes: string[], forceRefresh: boolean = false) => {
    setIsLoading(true);

    if (riskAnalysisId) {
      const body = forceRefresh ? { codes: riskCodes, force_refresh: true } : riskCodes;
      putRiskData(cuit || '', id || '', body, riskAnalysisId).then(
        (response) => {
          if (hasData(response) && response.status === 200) {
            setRiskData(response.data.risks);
          } else if (hasError(response).isAnyError) {
            useToast.error({
              title:
                'Ocurrió un error al consultar la información del balance.',
              id: 'risk-toast',
              description: hasError(response).isClientError
                ? 'Error del cliente'
                : 'Error del servidor',
              closeButton: true,
              autoClose: 5000,
            });
          }
          setIsLoading(false);
        }
      );
    } else {
      postRiskData(cuit || '', id || '', riskCodes).then((response) => {
        if (hasData(response) && response.status === 201) {
          setRiskAnaliysisId(response.data.id);
          setRiskData(response.data.risks);
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: 'Ocurrió un error al consultar la información del balance.',
            id: 'risk-toast',
            description: hasError(response).isClientError
              ? 'Error del cliente'
              : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
        setIsLoading(false);
      });
    }
  };

  const handleUpdateDebts = (codes: string[]) => {
    if (debts?.code && cuit && id) {
      updateDebts(cuit, id, debts.code, codes).then((response) => {
        if (hasData(response) && response.status === 200) {
          setDebts(response.data);
          useToast.success({
            title: 'Deudas actualizadas correctamente',
            id: 'debts-toast',
            description: '',
            closeButton: true,
            autoClose: 3000,
          });
        } else if (hasError(response).isAnyError) {
          useToast.error({
            title: 'Ocurrió un error al actualizar las deudas.',
            id: 'debts-toast',
            description: hasError(response).isClientError
              ? 'Error del cliente'
              : 'Error del servidor',
            closeButton: true,
            autoClose: 5000,
          });
        }
      });
    }
  };

  const handleRiskDataAndDebts = () => {
    handleRiskData(getEnabledRiskCodes([RISK_CODES.nosis, RISK_CODES.casfog, RISK_CODES.bcra]), true);
    handleUpdateDebts(['CENTRAL_DEUDORES', 'CENTRAL_DEUDORES_PLUS']);
  };

  const steps = [
    {
      id: 1,
      value: 'Semáforo',
      element: (
        <CheckLight
          handleNextStep={() => {
            goToNextStep();
            updateMaxStepReached(2);
          }}
          riskData={riskData}
          isLoading={isLoading}
          handleRiskData={handleRiskData}
          handleUpdateDebts={handleUpdateDebts}
          handleRiskDataAndDebts={handleRiskDataAndDebts}
          cuit={cuit}
          applicationId={id}
        />
      ),
    },
    {
      id: 2,
      value: 'Carga de balance',
      element: (
        <BalanceLoader
          cuit={cuit}
          applicationId={id}
          balanceInfo={balanceInfo}
          handleNextStep={(e: any) => {
            setBalanceData(e);
            goToNextStep();
            updateMaxStepReached(3);
          }}
          handlePurchasesSalesData={(e: any) => setPurchasesSalesData(e)}
          setBalanceInfo={(e: any) => {
            setBalanceInfo({
              ...(e.values as balanceInfo),
              ...formatNumbers(e.values),
            });
          }}
          setLocalBalanceId={(e: any) => setLocalBalanceId(e)}
          localBalanceId={localBalanceId}
          applicationStatus={parseInt(status)}
        />
      ),
    },
    {
      id: 3,
      value: 'Actividad post-balance',
      element: (
        <PostBalanceActivity
          cuit={cuit}
          applicationId={id}
          handleNextStep={() => {
            goToNextStep();
            updateMaxStepReached(4);
          }}
          handleRiskData={(e: any) => handleRiskData(e)}
          purchasesSalesData={purchasesSalesData}
          postBalanceData={postBalanceData}
          setPostBalanceData={(e: any) => setPostBalanceData(e)}
          applicationStatus={parseInt(status)}
          balanceLastMonth={{
            to_year: balanceInfo?.to_year,
            to_month: balanceInfo?.to_month,
          }}
        />
      ),
    },
    {
      id: 4,
      value: 'Resumen de riesgo',
      element: (
        <RiskResume
          riskData={riskData}
          isLoading={isLoading}
          handleRiskData={handleRiskData}
          handleRiskDataAndDebts={handleRiskDataAndDebts}
          cuit={cuit}
          applicationId={id}
          files={client?.files}
          legalEntity={client?.basic_data.legal_entity}
          applicationStatus={parseInt(status)}
          debts={debts}
          declineOrChangeBtn={!showOnlyRiskResume}
          hideInfo={hideCommiteInfo}
          balanceData={balanceInfo}
          postBalanceData={postBalanceData}
          clientData={client}
        />
      ),
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      height='-webkit-fill-available'
      width='85vw'
      spacing={'4'}
    >
      <Toast id='risk-toast' />
      {showStepBar && (
        <Card>
          <Card display='flex' margin={{ left: '2', bottom: '4' }} gap='10px'>
            <Icon
              name='chevron_left_outline'
              height={24}
              width={24}
              cursor='pointer'
              onClick={() => navigate(routes.risksAnalysis)}
            />
            <H withMargin={false} type='2' weight='bold' modifier='strong'>
              {client
                ? 'Análisis de riesgos de ' +
                  capitalize(client.basic_data.company_name) +
                  ' ( ' +
                  cuitMask(client.cuit) +
                  ' )'
                : 'Análisis de riesgos '}
            </H>
          </Card>
          <Card display='flex' justifyContent='center' width='100%'>
            <Card display='flex' width='60vw'>
              {steps.map((step, index) => (
                <Step
                  widthContainer='20%'
                  widthCircle1='200px'
                  widthId='48px'
                  heightId='100%'
                  widthCircle2='-webkit-fill-available'
                  step={step}
                  stepsLength={steps.length}
                  index={index}
                  currentStep={currentStep}
                  changeStep={(step) => changeStep(step)}
                />
              ))}
            </Card>
          </Card>
        </Card>
      )}

      {showOnlyRiskResume
        ? steps[steps.length - 1].element
        : steps[currentStep - 1].element}
    </Card>
  );
};

export default RiskAnalysis;
